import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Search, User, Briefcase, ArrowUp, ArrowDown, ChevronUp, ChevronDown } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigation = [
    // { name: 'Home', href: '/' },
    // { name: "About", href: "/about" },
    // { name: 'Services', href: '/services' },
    // { name: "Contact", href: "/contact" },
  ];

  const services = [
    { name: "Talent Acquisition", href: "/talent-acquisition" },
    { name: "HR Consulting", href: "/hr-consulting" },
    { name: "Contract Staffing", href: "/contract-staffing" },
    { name: "Tech Training", href: "/tech-training" },
    { name: "Employer Branding", href: "/employer-branding" },
    { name: "Job Matching", href: "/job-matching" },
    { name: "Digital First", href: "/digital-first" },
    { name: "Cloud First", href: "/cloud-first" },
    { name: "AI Powered", href: "/ai-powered" },
    { name: "Edge Ready", href: "/edge-ready" },
    { name: "For Companies", href: "/for-companies" },
    { name: "For Job Seekers", href: "/for-job-seekers" },
    { name: "For Employers", href: "/for-employers" },
    { name: "For Contractors", href: "/for-contractors" },
    { name: "CV Upload", href: "/cv-upload" },
    { name: "Submit Job", href: "/submit-job" },
    { name: "Success Stories", href: "/success-stories" },
  ];

  const isActive = (path) => location.pathname === path;

  // Add left header items
  const leftHeaderItems = [
    { name: "Cloud First", href: "/cloud-first" },
    { name: "Digital First", href: "/digital-first" },
    { name: "AI Powered", href: "/ai-powered" },
    { name: "Edge Ready", href: "/edge-ready" },
  ];

  // Add About dropdown items
  const aboutUs = [
    { name: "About Us", href: "/about" },
    { name: "Contact Us", href: "/contact" },
  ];

  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        // isScrolled
        //   ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200'
        //   :
        "bg-gradient-auth"
      }`}
    >
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20 w-full">
          {/* Left header items + Logo */}
          <div className="flex items-center space-x-6">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              {/* <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">T</span>
              </div> */}
              <span className="text-xl lg:text-3xl font-bold text-white">
                TechMind
              </span>
            </Link>

            {/* Left header items */}
            <nav className="hidden lg:flex items-center space-x-6">
              {leftHeaderItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`relative px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive(item.href)
                      ? "text-blue-400"
                      : "text-white hover:text-[#00AEEF]"
                  }`}
                >
                  {item.name}
                  {isActive(item.href) && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400"
                      initial={false}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 30,
                      }}
                    />
                  )}
                </Link>
              ))}
            </nav>
          </div>

          {/* Desktop Navigation (right side) */}
          <nav className="hidden lg:flex items-center space-x-6">
            {/* About Dropdown */}
            <div className="relative group">
              <button className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-white hover:text-[#00AEEF] transition-colors duration-200">
                <span>About</span>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[220px] bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top " style={{zIndex:'1000'}}>
                <div className="py-2 px-2 flex flex-col">
                  {aboutUs.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="block px-3 py-2 text-base text-grey-700 hover:text-[#00AEEF] rounded transition-colors duration-150 whitespace-nowrap"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`relative px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  isActive(item.href)
                    ? "text-blue-400"
                    : "text-white hover:text-[#00AEEF]"
                }`}
              >
                {item.name}
                {isActive(item.href) && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400"
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </Link>
            ))}

            {/* Services Dropdown */}
            <div className="relative group">
              <button className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-white hover:text-[#00AEEF] transition-colors duration-200">
                <span>Services</span>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[550px] bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top z-50">
                <div className="py-6 px-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-3">
                  {services.map((service, idx) => (
                    <Link
                      key={service.name}
                      to={service.href}
                      className="block px-2 py-2 text-base text-grey-700 hover:text-[#00AEEF] rounded transition-colors duration-150 whitespace-nowrap"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* careers */}
            {[{ name: "Careers", href: "/careers" }].map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`relative px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  isActive(item.href)
                    ? "text-blue-400"
                    : "text-white hover:text-[#00AEEF]"
                }`}
              >
                {item.name}
                {isActive(item.href) && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400"
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-4 ml-4">
            <Link
              to="/submit-job"
              className="inline-flex items-center px-4 py-2  text-sm font-medium rounded-md text-white bg-gradient-hero hover:bg-blue-700 transition-colors duration-200"
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Post a Job
            </Link>

            {/* {user ? (
              <div className="flex items-center space-x-4">
                <Link
                  to="/job-applications"
                  className="text-gray-700 hover:text-[#00AEEF] transition-colors duration-200"
                >
                  Applications
                </Link>
                <button
                  onClick={logout}
                  className="text-gray-700 hover:text-[#00AEEF] transition-colors duration-200"
                >
                  Logout
                </button>
              </div>
            ) : (
              <Link
                to="/moderator-login"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
              >
                <User className="w-4 h-4 mr-2" />
                Login
              </Link>
            )} */}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-[#00AEEF] hover:bg-gray-100 transition-colors duration-200"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-200"
          >
            <div className="px-4 py-6 space-y-4">
              {/* Left header items for mobile */}
              <div className="flex flex-wrap gap-2 mb-4">
                {leftHeaderItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className="block px-3 py-2 text-base font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors duration-200"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>

              {/* Static Links */}
              {navigation.map((item) =>
                item.name !== "About" && item.name !== "Services" ? (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className={`block px-3 py-2 text-base font-medium rounded-md transition-colors duration-200 ${
                      isActive(item.href)
                        ? "text-[#00AEEF] bg-blue-50"
                        : "text-gray-700 hover:text-[#00AEEF] hover:bg-gray-50"
                    }`}
                  >
                    {item.name}
                  </Link>
                ) : null
              )}

              {/* Collapsible About */}
              <div className="border-t border-gray-200 pt-4">
                <button
                  onClick={() => setIsAboutOpen(!isAboutOpen)}
                  className="w-full text-left px-3 py-2 text-base font-medium text-gray-700 flex justify-between items-center"
                >
                  About
                  <span>{isAboutOpen ? <ChevronUp/> : <ChevronDown/>}</span>
                </button>
                {isAboutOpen && (
                  <div className="pl-4 pt-2">
                    <Link
                      to="/about"
                      onClick={() => setIsOpen(false)}
                      className="block px-3 py-1 text-base text-gray-700 hover:text-[#00AEEF]"
                    >
                      About Us
                    </Link>
                    <Link
                      to="/contact"
                      onClick={() => setIsOpen(false)}
                      className="block px-3 py-1 text-base text-gray-700 hover:text-[#00AEEF]"
                    >
                      Contact Us
                    </Link>
                  </div>
                )}
              </div>

              {/* Collapsible Services */}
              <div className="border-t border-gray-200 pt-4 ">
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className="w-full text-left px-3 py-2 text-base font-medium text-gray-700 flex justify-between items-center"
                >
                  Services
                  <span>{isServicesOpen ? <ChevronUp/> : <ChevronDown/>}</span>
                </button>
                {isServicesOpen && (
                  <div className="pl-4 pt-2 h-[250px] overflow-y-scroll">
                    {services.map((service) => (
                      <Link
                        key={service.name}
                        to={service.href}
                        onClick={() => setIsOpen(false)}
                        className="block px-3 py-1 text-base text-gray-700 hover:text-[#00AEEF]"
                      >
                        {service.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

               {/* Collapsible About */}
               <div className="border-t border-gray-200 pt-4">
                <Link 
                to='/careers'
                onClick={() => setIsOpen(false)}
                      className="block px-3 py-1 text-base text-gray-700 hover:text-[#00AEEF]"
                >
                {/* <button
                  className="w-full text-left px-3 py-2 text-base font-medium text-gray-700 flex justify-between items-center"
                > */}
                  Careers
                {/* </button> */}
                </Link>
              </div>

              {/* Other Actions */}
              <div className="border-t border-gray-200 pt-4 space-y-3">
                <Link
                  to="/submit-job"
                  onClick={() => setIsOpen(false)}
                  className="block w-full text-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-hero hover:bg-blue-700 transition-colors duration-200"
                >
                  Post a Job
                </Link>

                {/* {user ? (
                  <div className="space-y-2">
                    <Link
                      to="/job-applications"
                      onClick={() => setIsOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:text-[#00AEEF]"
                    >
                      Applications
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setIsOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:text-[#00AEEF]"
                    >
                      Logout
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/moderator-login"
                    onClick={() => setIsOpen(false)}
                    className="block w-full text-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                  >
                    Login
                  </Link>
                )} */}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
